import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { StepCssClass } from '../models/clux/model';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, tap } from 'rxjs';
import _ from 'lodash';
import { Step } from './stepModel';
import { TimeoutService } from '@app/timeout.service';

// feel free to add to this enum in the future if needed. Right now we only use this one flag if we want to abort moving to the next step
export enum TeardownResult {
  Abort = 'Abort', // use when attempting to abort the next() method
}

export interface IStepViewModel<S, E> {
  entity: E;
  // used to communicate from components to model when user is aborting the flow
  onCancel?: Subject<void>;
  // used to communicate from model to components when flow is completed
  done?: Subject<{ errors?: string }>;

  // Contains CSS mapping/init/teardown/etc. for each step
  stepViews: Map<S, IStep<S>>;

  currentStep: S;
  // list of steps, IN ORDER
  steps: S[];

  currentForm?: UntypedFormGroup;

  formBuilder: UntypedFormBuilder;

  // navigates to the next step
  next(): void;

  // navigates to the previous step
  back(): void;

  // navigates to any step, validating if it's visitable beforehand
  navigate(stepIdx: number): void;

  // initializes any data need for navigating to a step
  initializeNext(): S | void;

  // executes logic after a step is completed
  teardown(parent?: StepViewModel<S, unknown>): Promise<S | TeardownResult | void> | S | TeardownResult | void;

  // submits all data to UBA
  submit(saveAndContinue?: boolean): Observable<{ errors?: string }>;
}

export interface IStep<S> {
  step?: S;
  valid: boolean;
  visitable: boolean;
  isSavedToEntity: boolean;
  stepCssClass: StepCssClass;
  form?: UntypedFormGroup;
  substep: number;
  numSubsteps: number;

  initialize(parent?: StepViewModel<S, unknown>): S | void;
  teardown(parent?: StepViewModel<S, unknown>): Promise<void | S | TeardownResult> | void | S | TeardownResult;
}

/**
 * A generic class for implementing step flows with shared logic.
 * All of this logic here is overwritable, and you are encouraged to do so if requirements demand it,
 * as this is simply a way of consolidating code that often gets duplicated.
 *
 * The generic type S represents an enum of steps that we're going to be progressing through with the model.
 * E represents an interface of an Entity in UBA that we'll be saving.
 */
export class StepViewModel<S, E> implements IStepViewModel<S, E> {
  public entity: E;
  public steps: S[];
  public stepViews = new Map<S, IStep<S>>();
  public done = new Subject<{ errors?: string }>();
  public cancel = new Subject<void>();
  public currentStep: S;
  public currentStepIdx = -1;
  public currentForm: UntypedFormGroup;
  public title: string;
  public direction = 0;
  public submitDisabled = false;
  public isCompleted = false;
  public buttons = {
    back: true,
    continue: true,
    saveAndContinue: false,
  };

  public nextButtonText: string;
  public saveAndContinueButtonText: string;
  public backButtonText: string;

  public customNextHandler?: () => void;
  public customBackHandler?: () => void;

  public get currentStepView(): IStep<S> {
    return this.stepViews.get(this.currentStep);
  }

  public get nextStepView(): IStep<S> | undefined {
    return this.currentStepIdx + 1 < this.steps.length ? this.stepViews.get(this.steps[this.currentStepIdx + 1]) : undefined;
  }

  constructor(
    steps: Array<S | IStep<S>>,
    public formBuilder: UntypedFormBuilder,
    public timeoutService: TimeoutService
  ) {
    this.steps = [];
    steps.forEach((step) => {
      if (this.isIStep(step)) {
        this.stepViews.set(step.step, step);
        this.steps.push(step.step);
      } else {
        const newStep = new Step(step as S);
        this.stepViews.set(step as S, newStep);
        this.steps.push(step as S);
      }
    });
  }

  public onCancel?: Subject<void>;

  private newStep = (stepIdx: number) => {
    this.currentStepIdx = stepIdx;
    this.currentStep = this.steps[this.currentStepIdx];
  };

  public async next(): Promise<void> {
    if (this.isCompleted) {
      return;
    }
    if (this.customNextHandler) {
      this.customNextHandler();
      return;
    }
    this.direction = 1;
    if (this.currentStepIdx !== -1 && this.currentStepView) {
      if (this.currentStepView.substep < this.currentStepView.numSubsteps) {
        await this.navigate(this.currentStepIdx, this.currentStepView.substep + 1);
        return;
      } else {
        this.currentStepView.stepCssClass = StepCssClass.Done;
        this.currentStepView.isSavedToEntity = true;
      }
    }
    const nextStep = this.steps[this.currentStepIdx + 1];
    if (this.currentStepIdx < this.steps.length && nextStep) {
      const nextStepView = this.stepViews.get(nextStep);
      if (nextStepView) {
        nextStepView.visitable = true;
      }
    }
    await this.navigate(this.currentStepIdx + 1);
  }

  public async back(): Promise<void> {
    if (this.customBackHandler) {
      this.customBackHandler();
      return;
    }
    this.direction = -1;
    if (this.currentStepView.substep > 0) {
      await this.navigate(this.currentStepIdx, this.currentStepView.substep - 1);
    } else {
      if (!this.currentStepView.isSavedToEntity) {
        this.currentStepView.visitable = false;
        this.currentStepView.stepCssClass = StepCssClass.Default;
      } else {
        this.currentStepView.stepCssClass = StepCssClass.Active;
      }
      await this.navigate(this.currentStepIdx - 1);
    }
  }

  /**
   * this method must be overwritten by the extender, otherwise it will always return an errors observable and do nothing
   * @returns the response from the backend, if any
   */
  public submit(): Observable<{ errors?: string }> {
    return of({ errors: 'not implemented!' });
  }

  /**
   * If the save and continue button is used, this method must be overwritten by the extender.
   * We use a subscription here because we don't subscribe to this response anywhere else in the flow, unlike the submit function
   */
  public saveAndContinue(): Subscription {
    return of(new Error('save and continue was called but not implemented')).subscribe();
  }

  public initializeNext(): S | void {
    return this.nextStepView?.initialize(this);
  }

  public teardown(): Promise<void | S | TeardownResult> | void | S | TeardownResult {
    return this.currentStepView?.teardown(this);
  }

  public async navigate(stepIdx: number, substepIdx: number = -1): Promise<void> {
    this.timeoutService.resetIdleTimer(); // keep user logged in while interacting with bcs renewal process, since bcs renewal can be a long process taking longer than 15 mins without "real" navigation

    // check termination cases first
    const fromStepIdx = this.currentStepIdx;
    if (stepIdx === -1) {
      this.cancel.next();
      return;
    } else if (stepIdx === this.steps.length) {
      this.isCompleted = true;

      this.submit()
        .pipe(
          tap((res) => {
            this.isCompleted = res.errors?.length === 0;
            this.done.next(res);
          }),
          catchError((e: unknown) => {
            this.isCompleted = false;
            this.done.hasError = true;
            this.done.next(e);
            return of(null);
          })
        )
        .subscribe();
      return;
    }

    const step = this.steps[stepIdx];
    if (!step) {
      return;
    }

    // check if the step we're navigating to is visitable
    const stepView = this.stepViews.get(step);
    if (!stepView || !stepView.visitable) {
      return;
    }

    // teardown current step
    let teardownResult: S | TeardownResult | void | undefined;
    if (this.currentStepView) {
      teardownResult = await Promise.resolve(this.teardown());
      if (teardownResult) {
        if (teardownResult === TeardownResult.Abort) {
          this.direction = 0;
          return;
        }
        if (this.steps[stepIdx] !== teardownResult) {
          const redirectIndex = this.steps.findIndex((s) => s === teardownResult);
          if (redirectIndex >= 0) {
            stepIdx = redirectIndex;
          }
        }
      }

      // clear old “Active” on the previous step:
      // if it was saved → Done, otherwise → Default
      const prevKey = this.steps[this.currentStepIdx];
      const prevView = this.stepViews.get(prevKey);
      if (prevView) {
        prevView.stepCssClass = prevView.isSavedToEntity ? StepCssClass.Done : StepCssClass.Default;
      }
    }

    let initializeResult: S | void;

    if (substepIdx !== -1 && this.currentStepView) {
      // Only update the substep of the current view
      this.currentStepView.substep = substepIdx;
      initializeResult = this.currentStepView.initialize(this);
    } else {
      const targetStep = this.steps[stepIdx];
      const targetStepView = this.stepViews.get(targetStep);

      if (!targetStepView) {
        return; // No step view found for step index
      }

      this.newStep(stepIdx);

      initializeResult = targetStepView.initialize(this);
    }

    const currentStepView = this.stepViews.get(this.currentStep);

    if (!currentStepView) {
      return; // No step view found for current step after initialization
    }

    // If the current step is not valid, navigate away
    if (!currentStepView.valid) {
      await this.navigate(fromStepIdx < stepIdx ? stepIdx - 1 : stepIdx + 1);
      return;
    }

    // If initialization redirected to a different step
    if (initializeResult && initializeResult !== this.currentStep) {
      const redirectView = this.stepViews.get(initializeResult);
      if (!redirectView) {
        return; // Initialization redirected to a step that doesn't have a view
      }

      redirectView.visitable = true;
      let i = this.currentStepIdx;
      while (i < this.steps.length && i > -1) {
        if (this.steps[i] === initializeResult) {
          await this.navigate(i);
          return;
        } else {
          const iteratedStepView = this.stepViews.get(this.steps[i]);
          if (iteratedStepView) {
            iteratedStepView.valid = fromStepIdx < this.currentStepIdx;
          }
        }
        i += fromStepIdx < this.currentStepIdx ? -1 : 1; // decrement if we're going back, increment otherwise
      }
    }

    // Get the current view again after all possible redirections
    const finalCurrentView = this.stepViews.get(this.currentStep);
    if (finalCurrentView) {
      this.currentForm = finalCurrentView.form;
      finalCurrentView.stepCssClass = StepCssClass.Active;
    }

    this.direction = 0;
    window.scrollTo(0, 0);
  }

  private isIStep(step: S | IStep<S>): step is IStep<S> {
    return (step as IStep<S>).step !== undefined;
  }
}
